import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Table, Button } from "reactstrap";
import { buildDashboardUrl } from "../utils/links";
import { WINDOW_LABELS_LONG } from "../utils/windows";

interface StockRow {
  symbol: string;
  symbol_name?: string;
  score: number;
  start_date: string;
  pricedLastDay: string;
  pricedResolution: string;
  priceChange: number;
  window: number;
  origin_window: number;
  forecast_window: number;
}

interface Props {
  data: StockRow[];
}

const ModernStockTable: React.FC<Props> = ({ data }) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: string;
  } | null>({
    key: "score",
    direction: "desc",
  });

  // Sort data based on current sortConfig
  const sortedData = React.useMemo(() => {
    if (!sortConfig) return data;

    const sorted = [...data];
    sorted.sort((a, b) => {
      const aValue = a[sortConfig.key as keyof typeof a];
      const bValue = b[sortConfig.key as keyof typeof b];

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });

    return sorted;
  }, [data, sortConfig]);

  // Handle column sorting
  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "desc" };
    });
  };

  // Handle scroll to top when clicking on a stock link
  const handleLinkClick = () => {
    window.scrollTo(0, 0);
  };

  // CSV export function
  const exportToCSV = () => {
    const headers = [
      "#,Symbol,Name,Match Score,Start Date,Origin Horizon,Pattern End,Forecast End,% Change",
    ];
    const rows = sortedData.map((row, i) => [
      i + 1,
      row.symbol,
      row.symbol_name || "",
      row.score.toFixed(2),
      row.start_date,
      WINDOW_LABELS_LONG[row.origin_window] ?? `${row.origin_window} days`,
      row.pricedLastDay,
      row.pricedResolution,
      `${row.priceChange.toFixed(2)}%`,
    ]);
    const csvContent = [headers, ...rows].map((e) => e.join(",")).join("\n");

    // Create Blob and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "pattern_matches.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="modern-stock-table">
      <div className="table-header">
        <h3>Match Details</h3>
        <Button onClick={exportToCSV} className="export-btn">
          <span className="export-icon"></span>
          Export to CSV
        </Button>
      </div>

      <div className="table-responsive">
        <Table hover className="matches-table">
          <thead>
            <tr>
              <th className="rank-column">#</th>
              <th
                className={`sortable-column ${sortConfig?.key === "symbol" ? `sorted-${sortConfig.direction}` : ""}`}
                onClick={() => handleSort("symbol")}
              >
                Symbol
                <span className="sort-icon"></span>
              </th>
              <th
                className={`sortable-column ${sortConfig?.key === "symbol_name" ? `sorted-${sortConfig.direction}` : ""}`}
                onClick={() => handleSort("symbol_name")}
              >
                Name
                <span className="sort-icon"></span>
              </th>
              <th
                className={`sortable-column ${sortConfig?.key === "score" ? `sorted-${sortConfig.direction}` : ""}`}
                onClick={() => handleSort("score")}
              >
                Match Score
                <span className="sort-icon"></span>
              </th>
              <th
                className={`sortable-column ${sortConfig?.key === "start_date" ? `sorted-${sortConfig.direction}` : ""}`}
                onClick={() => handleSort("start_date")}
              >
                Start Date
                <span className="sort-icon"></span>
              </th>
              <th
                className={`sortable-column ${sortConfig?.key === "origin_window" ? `sorted-${sortConfig.direction}` : ""}`}
                onClick={() => handleSort("origin_window")}
              >
                Origin Horizon
                <span className="sort-icon"></span>
              </th>
              <th className="price-column">Pattern End</th>
              <th className="price-column">
                +{data[0]?.forecast_window} Days
              </th>
              <th
                className={`sortable-column price-change-column ${sortConfig?.key === "priceChange" ? `sorted-${sortConfig.direction}` : ""}`}
                onClick={() => handleSort("priceChange")}
              >
                % Change
                <span className="sort-icon"></span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, i) => (
              <tr key={`row-${i}`}>
                <td className="rank-column">{i + 1}</td>
                <td className="symbol-column">
                  <Link
                    to={buildDashboardUrl({ ticker: row.symbol, window: row.window })}
                    rel="nofollow"
                    onClick={handleLinkClick}
                    className="stock-link"
                  >
                    {row.symbol}
                  </Link>
                </td>
                <td className="symbol-column">
                  {row.symbol_name || row.symbol}
                </td>
                <td>
                  <div className="score-display">
                    <div className="score-bar">
                      <div
                        className="score-fill"
                        style={{ width: `${row.score}%` }}
                      ></div>
                    </div>
                    <span className="score-value">{row.score.toFixed(2)}%</span>
                  </div>
                </td>
                <td className="date-column">{row.start_date}</td>
                <td>{WINDOW_LABELS_LONG[row.origin_window] ?? `${row.origin_window} days`}</td>
                <td className="price-column">{row.pricedLastDay}</td>
                <td className="price-column">{row.pricedResolution}</td>
                <td
                  className={`price-change-column ${row.priceChange >= 0 ? "positive" : "negative"}`}
                >
                  {row.priceChange >= 0 ? "▲" : "▼"}{" "}
                  {Math.abs(row.priceChange).toFixed(2)}%
                </td>
              </tr>
            ))}

            {sortedData.length === 0 && (
              <tr>
                <td colSpan={9} className="no-data">
                  No matching patterns found. Try adjusting your search
                  parameters.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <div className="table-footer">
        <div className="results-summary">
          Showing {sortedData.length} pattern matches
        </div>
      </div>
    </div>
  );
};

export default ModernStockTable;
